import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Divider, Flex } from '@televet/kibble-ui/build/chakra';
import { IconName } from '@televet/kibble-ui/build/components/Icon';
import { Text } from '@televet/kibble-ui/build/components/Text';
import {
  PaymentPimsWritebackAction,
  PimsWritebackStatus,
  PimsWritebackType,
  useCheckCanManuallyRetryPaymentWritebackQuery,
  useCreateOnePimsWritebackLogEntryMutation,
  useJobChainCompletedSubscription,
  useRetryPaymentWritebackMutation,
} from 'shared/types/graphql';
import { WritebackStatusDisplay, writebackStatusDisplayValue } from '../constants';
import { history, RouteBasePaths, SettingsRoutes } from 'routes';
import { FeatureFlagName } from 'shared/enums';
import * as Sentry from '@sentry/react';
import useFeatureFlag from 'shared/hooks/useFeatureFlag';
import useClinicUser from 'shared/hooks/useClinicUser';
import { getWritebackStatus } from '../utils';
import { WritebackStatusColor } from '../records/writeback.records';
import { IWritebackStatus } from '../utils';
import { Button, Tooltip } from '@televet/kibble-ui';
import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';
import { differenceInMilliseconds } from 'date-fns';

interface IWritebackStatusProps {
  writebackStatus: IWritebackStatus;
  paymentIntentId: string;
  popoverDescription?: string;
  isPaymentConfigInvalid: boolean;
  isIntegratedPayment: boolean;
}

const WritebackStatus = ({
  writebackStatus,
  paymentIntentId,
  popoverDescription,
  isPaymentConfigInvalid,
  isIntegratedPayment,
}: IWritebackStatusProps): JSX.Element => {
  const { isFeatureEnabled } = useFeatureFlag();
  const { primaryIntegration } = useIntegrationsProvider();
  const { hasAdminRoleAtCurrentClinic } = useClinicUser();
  const [manualRetryInProgress, setManualRetryInProgress] = useState(false);
  const [isInRetryPeriod, setIsInRetryPeriod] = useState<boolean>(false);
  const [retryStatus, setRetryStatus] = useState<IWritebackStatus | undefined>();
  const [retryWriteback, { data: retryWritebackData }] = useRetryPaymentWritebackMutation();
  const [createPaymentWriteback] = useCreateOnePimsWritebackLogEntryMutation();

  const currentWritebackStatus = retryStatus || writebackStatus;

  const statusDisplay = useMemo(() => writebackStatusDisplayValue[currentWritebackStatus], [currentWritebackStatus]);

  const { data: canRetryWritebackData, refetch } = useCheckCanManuallyRetryPaymentWritebackQuery({
    variables: { paymentIntentId },
    skip: statusDisplay !== WritebackStatusDisplay.Failed,
  });

  const isRetrying = manualRetryInProgress || isInRetryPeriod;

  useJobChainCompletedSubscription({
    variables: { jobChainId: retryWritebackData?.retryPaymentWriteback?.jobChainId || '' },
    onData: (data) => {
      const writeback = data.data.data?.jobChainCompleted?.payload?.[0];
      if (writeback) {
        const _writebackStatus = getWritebackStatus({
          writebackStatus: writeback.status,
          writebackError: writeback.error,
          isPaymentConfigInvalid,
          isIntegratedPayment,
        });
        setRetryStatus(_writebackStatus);
        refetch();
      }
      setManualRetryInProgress(false);
    },
    skip: !retryWritebackData?.retryPaymentWriteback?.jobChainId,
  });

  useEffect(() => {
    const data = canRetryWritebackData?.canManuallyRetryPaymentWriteback;
    let timeoutId: NodeJS.Timeout;
    if (data) {
      const retryAvailableAfter = data.retryAvailableAfter;
      const isAfterRetryPeriod = retryAvailableAfter && new Date() > retryAvailableAfter;
      setIsInRetryPeriod(!!retryAvailableAfter && !isAfterRetryPeriod);

      if (retryAvailableAfter) {
        timeoutId = setTimeout(() => {
          refetch();
        }, differenceInMilliseconds(new Date(retryAvailableAfter), new Date()) + 1000);
      }
    }
    return (): void => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [canRetryWritebackData, refetch]);

  const tagColor = useMemo(() => WritebackStatusColor[currentWritebackStatus], [currentWritebackStatus]);

  const { label, bgColor } = useMemo(() => {
    return {
      label: isRetrying ? 'Retrying' : statusDisplay || '',
      bgColor: isRetrying ? 'background.warning' : tagColor,
    };
  }, [isRetrying, statusDisplay, tagColor]);

  const canRetryWriteback =
    !!canRetryWritebackData?.canManuallyRetryPaymentWriteback.retryable && !manualRetryInProgress && !isInRetryPeriod;

  const { icon: tagIcon, label: actionLabel } = useMemo(() => {
    if (manualRetryInProgress) return { icon: undefined, label: undefined };
    switch (statusDisplay) {
      case WritebackStatusDisplay.Failed:
        return {
          icon: 'arrowCircle',
          label: 'Retry writeback',
        };
      case WritebackStatusDisplay.Invalid:
        return {
          icon: 'wrench',
          label: 'Fix payment settings',
        };
      default:
        return { icon: undefined, label: undefined };
    }
  }, [manualRetryInProgress, statusDisplay]);

  const description = useMemo(() => {
    return isRetrying
      ? 'Retry in progress'
      : (!canRetryWritebackData?.canManuallyRetryPaymentWriteback.retryable && popoverDescription) || '';
  }, [canRetryWritebackData?.canManuallyRetryPaymentWriteback.retryable, isRetrying, popoverDescription]);

  const handleRetryWriteback = useCallback(async () => {
    if (!isFeatureEnabled(FeatureFlagName.BitwerxPaymentWritebacks)) return;
    setManualRetryInProgress(true);
    try {
      await retryWriteback({
        variables: { stripePaymentIntentId: paymentIntentId },
      });
    } catch (e) {
      setManualRetryInProgress(false);
      console.error(e);
      Sentry.captureException(e);
    }
  }, [isFeatureEnabled, paymentIntentId, retryWriteback]);

  const goToPaymentSettingsPage = useCallback(() => {
    history.push(`${RouteBasePaths.Settings}${SettingsRoutes.ClinicPayment}`);
  }, []);

  const clickAction = useMemo(() => {
    if (manualRetryInProgress) return;

    switch (statusDisplay) {
      case WritebackStatusDisplay.Failed:
        return canRetryWriteback ? handleRetryWriteback : undefined;
      case WritebackStatusDisplay.Invalid:
        return hasAdminRoleAtCurrentClinic ? goToPaymentSettingsPage : undefined;
      default:
        return;
    }
  }, [
    manualRetryInProgress,
    statusDisplay,
    canRetryWriteback,
    handleRetryWriteback,
    hasAdminRoleAtCurrentClinic,
    goToPaymentSettingsPage,
  ]);

  const onMarkClick = useCallback(() => {
    createPaymentWriteback({
      variables: {
        data: {
          status: PimsWritebackStatus.Success,
          type: PimsWritebackType.Payment,
          integration: {
            connect: {
              id: primaryIntegration?.id || '',
            },
          },
          paymentLog: {
            create: {
              action: PaymentPimsWritebackAction.PaymentCollected,
              payment: {
                connect: {
                  id: paymentIntentId,
                },
              },
            },
          },
        },
      },
    })
      .then(() => {
        setRetryStatus(PimsWritebackStatus.Success);
      })
      .catch((e) => {
        console.error(e);
        Sentry.captureException(e);
      });
  }, [createPaymentWriteback, paymentIntentId, primaryIntegration?.id]);

  return (
    <Tooltip label={description} placement="top">
      <Flex maxW="130px" px={2} bg={bgColor} borderRadius="sm" alignItems="center">
        <Text size="sm">{label}</Text>
        {currentWritebackStatus !== PimsWritebackStatus.Success && (
          <>
            <Tooltip label={actionLabel || ''} placement="bottom">
              <Button
                variant="ghostNeutral"
                size="xs"
                iconName={(tagIcon || 'arrowCircle') as IconName}
                aria-label="Retry writeback"
                onClick={clickAction}
                isDisabled={isRetrying || !canRetryWriteback}
                isLoading={manualRetryInProgress}
                ml={1}
              />
            </Tooltip>
            <Divider orientation="vertical" height="15px" mx={1} />
            <Tooltip label="Mark writeback as successful if it was manually handled" placement="bottom">
              <Button
                variant="ghostNeutral"
                size="xs"
                iconName="checkmark"
                aria-label="Mark writeback as successful"
                isDisabled={isRetrying}
                onClick={onMarkClick}
              />
            </Tooltip>
          </>
        )}
      </Flex>
    </Tooltip>
  );
};

export default WritebackStatus;
